import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Table, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { formatDollars } from "capitalrx-utilities";

import { MedicarePrescriptionPaymentPlanEstimate } from "@/api/axios";
import { MONTHS } from "@/basic/utils/months";

interface Props {
  results: ReadonlyArray<MedicarePrescriptionPaymentPlanEstimate>;
  expectedMonthlyCosts: number;
}

const PaymentsTable: React.FC<Props> = ({ results, expectedMonthlyCosts }: Props) => {
  const { t: translate } = useTranslation();
  const rows = useMemo(
    () =>
      results.map((payment, i) => ({
        id: i,
        data: payment.estimation,
      })),
    [results],
  );

  const dataSource = useSimpleTableDataSource(rows);
  return (
    <Table dataSource={dataSource} className="payments-table">
      <Table.Custom name="month" title={translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentTable.month")} sortable initialWidth="span">
        {(value: unknown): ReactNode => {
          const monthNumber = new Date(value as string).getUTCMonth() + 1;
          const monthKey = MONTHS[monthNumber];
          return translate(`MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.${monthKey}`);
        }}
      </Table.Custom>
      <Table.Custom name="expectedMonthlyCost" sortable title={translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentTable.expectedMonthlyCost")} align="right" initialWidth="span">
        {(): ReactNode => formatDollars(expectedMonthlyCosts)}
      </Table.Custom>
      <Table.Custom name="true_out_of_pocket_amount" sortable title={translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentTable.outOfPocket")} align="right" initialWidth="span">
        {(value: unknown, _): ReactNode => {
          return formatDollars(value as string);
        }}
      </Table.Custom>
      <Table.Custom name="billed_amount" title={translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentTable.estimatedBill")} align="right" initialWidth="span">
        {(value: unknown, _): ReactNode => {
          return formatDollars(value as string);
        }}
      </Table.Custom>
    </Table>
  );
};

export default PaymentsTable;
